import type { TimePreset } from '../model/types.ts';
import type { PromptSettings } from './prompts.ts';

/** Wire format between the app and `/api/generate`. Shared by client and server. */

export type Sizing = 'custom' | 'fixed';

/**
 * How the server authenticates the app:
 * - 'token': per-install bearer token from POST /api/register (BETTER_LIGHTS_TOKEN_SECRET set)
 * - 'key':   legacy shared `x-better-lights-key` header (BETTER_LIGHTS_API_KEY set, no token secret)
 * - 'open':  no auth (local development)
 */
export type AuthMode = 'token' | 'key' | 'open';

export interface StatusResponse {
  configured: boolean;
  provider: 'openai' | 'azure' | null;
  model: string | null;
  sizing: Sizing;
  /** The server expects an `x-better-lights-key` header (legacy shared key). */
  requiresKey: boolean;
  auth: AuthMode;
  /** Images each install may generate per UTC day, when quotas are enforced. */
  dailyImageLimit: number | null;
}

export interface RegisterRequest {
  /** Random id generated once per app install. */
  installId: string;
  platform?: string;
}

export interface RegisterResponse {
  /** Bearer token for /api/generate, or null when the server runs without token auth. */
  token: string | null;
  dailyImageLimit: number | null;
}

/** Prompt settings on the wire: no free text (see PromptSettings). */
export type PromptOptions = PromptSettings;

export interface GenerateRequest {
  time: TimePreset;
  count: number;
  quality: 'medium' | 'high';
  size: { width: number; height: number };
  /** Base64 JPEG (no data: prefix), already at `size`. */
  photo: string;
  /** Base64 JPEG of the magenta placement guide, at `size`. */
  guide?: string;
  /** Base64 PNG edit mask (transparent = editable), at `size`. */
  mask?: string;
  options: PromptOptions;
}

export interface GenerateResponse {
  /** Base64 JPEGs. */
  images: string[];
  prompt: string;
  model: string;
  size: { width: number; height: number };
}

export interface ErrorResponse {
  error: string;
  code?: string;
  /** HTTP-equivalent status; set when the error arrives inside a streamed 200 response. */
  status?: number;
  /** Seconds until a rate limit resets. */
  retryAfter?: number;
}

/**
 * POST /api/generate streams whitespace heartbeats while the provider works (keeps mobile
 * networks, proxies and iOS's 60 s idle timeout from dropping the request), then one JSON
 * document: either the result or an error. Leading whitespace is valid JSON.
 */
export type GenerateResult = GenerateResponse | ErrorResponse;

export function isErrorResult(r: GenerateResult): r is ErrorResponse {
  return typeof (r as ErrorResponse).error === 'string';
}

export const MAX_IMAGES = 3;

/* ---------------------------------------------------------------------------------- */
/* Output sizing                                                                       */
/* ---------------------------------------------------------------------------------- */

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AiCanvas {
  width: number;
  height: number;
  /** Where the photo sits inside the canvas (letterboxing only when unavoidable). */
  content: Rect;
}

const FIXED_SIZES = [
  { width: 1024, height: 1024 },
  { width: 1536, height: 1024 },
  { width: 1024, height: 1536 },
];

/** GPT Image 2+ accepts any WxH with edges divisible by 16, aspect within 1:3..3:1. */
const MIN_PIXELS = 655_360;
const MAX_RATIO = 3;

const round16 = (n: number) => Math.max(16, Math.round(n / 16) * 16);

/**
 * Picks the AI canvas for a photo. With custom sizing the canvas matches the photo's
 * aspect ratio, so results line up pixel-for-pixel with the original for before/after.
 */
export function aiCanvasFor(photoW: number, photoH: number, sizing: Sizing, longSide = 1536): AiCanvas {
  const aspect = photoW / photoH;
  if (sizing === 'fixed') {
    const target = FIXED_SIZES.reduce((best, s) =>
      Math.abs(Math.log(s.width / s.height / aspect)) < Math.abs(Math.log(best.width / best.height / aspect)) ? s : best,
    );
    return { ...target, content: containRect(aspect, target.width, target.height) };
  }

  const ratio = Math.min(MAX_RATIO, Math.max(1 / MAX_RATIO, aspect));
  let width = ratio >= 1 ? longSide : longSide * ratio;
  let height = ratio >= 1 ? longSide / ratio : longSide;
  const area = width * height;
  if (area < MIN_PIXELS) {
    const k = Math.sqrt(MIN_PIXELS / area);
    width *= k;
    height *= k;
  }
  width = round16(width);
  height = round16(height);
  while (width * height < MIN_PIXELS) {
    width += 16;
    height = round16(width / ratio);
  }
  return { width, height, content: containRect(aspect, width, height) };
}

function containRect(aspect: number, width: number, height: number): Rect {
  if (width / height > aspect) {
    const w = height * aspect;
    return { x: (width - w) / 2, y: 0, width: w, height };
  }
  const h = width / aspect;
  return { x: 0, y: (height - h) / 2, width, height: h };
}
